import type { ChaveDoQuadro } from "@bookdental/schemas";
import { useQuadrosDaFicha } from "@/components/patients/home/use-quadros-da-ficha";
import { MenuDoQuadro } from "@/components/patients/home/MenuDoQuadro";
import { QuadroConsultas } from "@/components/patients/home/QuadroConsultas";
import { QuadroProteses } from "@/components/patients/home/QuadroProteses";
import { QuadroAtendimentos } from "@/components/patients/home/QuadroAtendimentos";
import { QuadroArquivos } from "@/components/patients/home/QuadroArquivos";
import { QuadroContato } from "@/components/patients/home/QuadroContato";
import { QuadroEmBreve } from "@/components/patients/home/QuadroEmBreve";
import { QuadroTratamentos } from "@/components/patients/home/QuadroTratamentos";
import type { PacienteDoQuadro } from "@/components/patients/home/tipos";

type Props = {
  patientId: string;
  paciente: PacienteDoQuadro;
  onScheduleAppointment: () => void;
  /** Ausentes quando a pessoa não pode pedir prótese / montar plano — o quadro fica sem o botão. */
  onPedirProtese?: () => void;
  onCriarPlano?: () => void;
  /** Troca a aba da ficha — destino dos "Ver todos" dos quadros. */
  onIrParaAba: (aba: string) => void;
};

/**
 * Aba Início da ficha: quatro posições fixas, cada uma com o quadro que a pessoa escolheu (salvo na
 * conta dela, igual pra todo paciente). O "⋯" de cada quadro troca por outro ou volta ao padrão.
 */
export function PatientHomeTab({ patientId, paciente, onScheduleAppointment, onPedirProtese, onCriarPlano, onIrParaAba }: Props) {
  const { naTela, disponiveis, trocar, restaurarPadrao, personalizado } = useQuadrosDaFicha();
  const foraDaTela = disponiveis.filter((c) => !naTela.includes(c));

  const quadro = (chave: ChaveDoQuadro, posicao: number) => {
    const menu = (
      <MenuDoQuadro
        atual={chave}
        opcoes={foraDaTela}
        personalizado={personalizado}
        onTrocar={(nova) => trocar(posicao, nova)}
        onRestaurar={restaurarPadrao}
      />
    );
    const base = { patientId, menu, irParaAba: onIrParaAba };
    switch (chave) {
      case "consultas":
        return <QuadroConsultas {...base} onAgendar={onScheduleAppointment} />;
      case "proteses":
        return <QuadroProteses {...base} onNovo={onPedirProtese} />;
      case "atendimentos":
        return <QuadroAtendimentos {...base} />;
      case "arquivos":
        return <QuadroArquivos {...base} />;
      case "contato":
        return <QuadroContato menu={menu} paciente={paciente} />;
      case "tratamentos":
        return <QuadroTratamentos {...base} onNovo={onCriarPlano} />;
      case "alertas":
        return <QuadroEmBreve tipo={chave} menu={menu} />;
    }
  };

  return (
    // grid-cols-1 limita a coluna à tela no celular: sem ele, nome comprido alargava o quadro além da borda.
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {naTela.map((chave, i) => (
        <div key={`${i}-${chave}`} className="flex flex-col [&>section]:flex-1">
          {quadro(chave, i)}
        </div>
      ))}
    </div>
  );
}
